import { ThemeEvents } from '@theme/events';

/**
 * <variant-more-products>
 *
 * Makes the "Wait, There's More" product rail variant-aware. The server preloads every variant's
 * rendered product-card HTML (sections/wait-theres-more.liquid) plus a default (parent) list; this
 * element swaps the visible cards to the selected variant's list on Horizon's native `variant:update`
 * event — no extra network request, matching how native PDP components react to variant changes.
 *
 * Resolution: a variant with its own list shows that list; a variant with none falls back to the
 * default (parent) list. The initial render is already correct (server-rendered for the selected
 * variant), so we only swap on subsequent variant changes.
 *
 * Why listen on `document`: this is its own section, so it can't scope to the product section that
 * dispatches the event. VariantUpdateEvent bubbles to `document`; we guard by matching the product id
 * so we never react to an unrelated product's variant change (mirrors accordion-variant-metafield.js).
 */
class VariantMoreProducts extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-wtm-list]');
    this.defaultHtml = this.#readJson('[data-wtm-default]', '');
    this.variantMap = this.#readJson('[data-wtm-variants]', {}) || {};
    // In the theme editor, never hide the section — merchants must be able to see and select it
    // even when the previewed variant resolves blank. Content still swaps for preview.
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

    // What's currently rendered. Seeded with the server-rendered variant so the `variant:update`
    // Horizon fires on page load (for that same variant) is recognised as a no-op and skipped —
    // re-inserting the cards needlessly re-initialises their image galleries and, on mobile, lands
    // them on a non-primary image. Only a REAL variant change swaps.
    this.renderedKey = this.dataset.selectedVariantId ? `variant:${this.dataset.selectedVariantId}` : null;

    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  /**
   * @param {string} selector
   * @param {*} fallback
   */
  #readJson(selector, fallback) {
    const el = this.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return fallback;
    }
  }

  /** @param {Event & { detail?: { resource?: { id?: number }, data?: { productId?: string, newProduct?: { id?: string } } } }} event */
  onVariantUpdate(event) {
    const detail = event.detail || {};
    const resource = detail.resource;
    const data = detail.data || {};
    if (!resource) return;

    // Only react to THIS product. A combined-listing switch to a different product isn't in our
    // preloaded map, so fall back to the default (parent) list rather than show a stale value.
    const myProductId = this.dataset.productId;
    const eventProductId = data.newProduct ? data.newProduct.id : data.productId;
    const differentProduct = Boolean(myProductId && eventProductId && String(eventProductId) !== String(myProductId));

    // Skip when nothing actually changed (notably the page-load variant:update for the already
    // server-rendered variant) — a redundant swap re-inits the card image galleries.
    const key = differentProduct ? `product:${eventProductId}` : `variant:${resource.id}`;
    if (key === this.renderedKey) return;
    this.renderedKey = key;

    if (differentProduct) {
      this.#apply(this.defaultHtml);
      return;
    }

    // A variant with its own override wins; otherwise fall back to the default (parent) list.
    let html = this.variantMap[String(resource.id)];
    if (html == null || html.trim() === '') html = this.defaultHtml;
    this.#apply(html);
  }

  /** @param {string} html */
  #apply(html) {
    const hasContent = Boolean(html && html.trim() !== '');
    if (this.list) this.list.innerHTML = hasContent ? html : '';
    // Hide the whole section (heading + rail) when the resolved list is empty.
    this.toggleAttribute('hidden', !(hasContent || this.designMode));
  }
}

if (!customElements.get('variant-more-products')) {
  customElements.define('variant-more-products', VariantMoreProducts);
}
